import * as React from 'react'
import { isNodeSelection } from '@tiptap/react'
import type { Editor } from '@tiptap/react'
import { useTiptapEditor } from 'src/hooks/use-tiptap-editor'
import { Button, ButtonProps } from '@components/SAPPEditor/tiptap-ui-primitive/button'
import { Separator } from '@components/SAPPEditor/tiptap-ui-primitive/separator'
import { isMarkInSchema } from '@utils/tiptap-utils'
import { Popover, PopoverContent, PopoverTrigger } from '@components/SAPPEditor/tiptap-ui-primitive/popover'
import { HighlighterIcon } from '../../tiptap-icons/highlighter-icon'
import { BanIcon } from '../../tiptap-icons/ban-icon'
import { useMenuNavigation } from 'src/hooks/use-menu-navigation'
import { ChevronDownIcon } from '../../tiptap-icons/chevron-down-icon'
import { CheckIcon } from '../../tiptap-icons/check-icon'


export interface HighlightColor {
  label: string
  value: string
  border?: string
}

export interface HighlightContentProps {
  editor?: Editor | null
  colors?: HighlightColor[]
  activeNode?: number
}

export const DEFAULT_HIGHLIGHT_COLORS: HighlightColor[] = [
  {
    label: 'Black-900',
    value: '#1A1A1A',
    border: '#999999',
  },
  {
    label: 'Black-400',
    value: '#999999',
    border: '#999999',
  },
  {
    label: 'Black-300',
    value: '#B3B3B3',
    border: '#B3B3B3',
  },
  {
    label: 'Black-200',
    value: '#CCCCCC',
    border: '#CCCCCC',
  },
  {
    label: 'Black-100',
    value: '#E6E6E6',
    border: '#E6E6E6',
  },
  {
    label: 'White',
    value: '#FFFFFF',
    border: '#FFFFFF',
  },
  // Red
  {
    label: 'Red-500',
    value: '#FF0000',
    border: '#FF0000',
  },
  {
    label: 'Red-50',
    value: '#FFE5E5',
    border: '#FFE5E5',
  },
  {
    label: 'Red-100',
    value: '#EA9999',
    border: '#EA9999',
  },
  {
    label: 'Red-700',
    value: '#CC0000',
    border: '#CC0000',
  },
  {
    label: 'Red-800',
    value: '#990000',
    border: '#990000',
  },
  {
    label: 'Red-900',
    value: '#660000',
    border: '#660000',
  },
 // Orange
  {
    label: 'Orange-500',
    value: '#FF9900',
    border: '#FF9900',
  },
  {
    label: 'Orange-50',
    value: '#FCE5CD',
    border: '#FCE5CD',
  },
  {
    label: 'Orange-100',
    value: '#F9CB9C',
    border: '#F9CB9C',
  },
  {
    label: 'Orange-700',
    value: '#E69138',
    border: '#E69138',
  },
  {
    label: 'Orange-800',
    value: '#B45F06',
    border: '#B45F06',
  },
  {
    label: 'Orange-900',
    value: '#783F04',
    border: '#783F04',
  },
  // Yellow
  {
    label: 'Yellow-500',
    value: '#FFFF00',
    border: '#FFFF00',
  },
  {
    label: 'Yellow-50',
    value: '#FFF2CC',
    border: '#FFF2CC',
  },
  {
    label: 'Yellow-100',
    value: '#FFE599',
    border: '#FFE599',
  },
  {
    label: 'Yellow-700',
    value: '#FFD466',
    border: '#FFD466',
  },
  {
    label: 'Yellow-800',
    value: '#BF9000',
    border: '#BF9000',
  },
  {
    label: 'Yellow-900',
    value: '#7F6000',
    border: '#7F6000',
  },
  // Green
  {
    label: 'Green-500',
    value: '#00FF00',
    border: '#00FF00',
  },
  {
    label: 'Green-50',
    value: '#D9EAD3',
    border: '#D9EAD3',
  },
  {
    label: 'Green-100',
    value: '#B6D7A8',
    border: '#B6D7A8',
  },
  {
    label: 'Green-700',
    value: '#6AA84F',
    border: '#6AA84F',
  },
  {
    label: 'Green-800',
    value: '#38761D',
    border: '#38761D',
  },
  {
    label: 'Green-900',
    value: '#274E13',
    border: '#274E13',
  },
  // Mint
  {
    label: 'Mint-500',
    value: '#00FFFF',
    border: '#00FFFF',
  },
  {
    label: 'Mint-50',
    value: '#D0E0E3',
    border: '#D0E0E3',
  },
  {
    label: 'Mint-100',
    value: '#A2C4C9',
    border: '#A2C4C9',
  },
  {
    label: 'Mint-700',
    value: '#45818E',
    border: '#45818E',
  },
  {
    label: 'Mint-800',
    value: '#134F5C',
    border: '#134F5C',
  },
  {
    label: 'Mint-900',
    value: '#0C343D',
    border: '#0C343D',
  },
  // Blue
  {
    label: 'Blue-500',
    value: '#4B86E8',
    border: '#4B86E8',
  },
  {
    label: 'Blue-50',
    value: '#C9DAF8',
    border: '#C9DAF8',
  },
  {
    label: 'Blue-100',
    value: '#6D9EEB',
    border: '#6D9EEB',
  },
  {
    label: 'Blue-700',
    value: '#3C78D8',
    border: '#3C78D8',
  },
  {
    label: 'Blue-800',
    value: '#1155CC',
    border: '#1155CC',
  },
  {
    label: 'Blue-900',
    value: '#1C4587',
    border: '#1C4587',
  },
  // Purple
  {
    label: 'Purple-500',
    value: '#9900FF',
    border: '#9900FF',
  },
  {
    label: 'Purple-50',
    value: '#D9D2E9',
    border: '#D9D2E9',
  },
  {
    label: 'Purple-100',
    value: '#B4A7D6',
    border: '#B4A7D6',
  },
  {
    label: 'Purple-700',
    value: '#674EA7',
    border: '#674EA7',
  },
  {
    label: 'Purple-800',
    value: '#351C75',
    border: '#351C75',
  },
  {
    label: 'Purple-900',
    value: '#20124D',
    border: '#20124D',
  },
  // Pink
  {
    label: 'Pink-500',
    value: '#FF00FF',
    border: '#FF00FF',
  },
  {
    label: 'Pink-50',
    value: '#EAD1DC',
    border: '#EAD1DC',
  },
  {
    label: 'Pink-100',
    value: '#D5A6BD',
    border: '#D5A6BD',
  },
  {
    label: 'Pink-700',
    value: '#A64D79',
    border: '#A64D79',
  },
  {
    label: 'Pink-800',
    value: '#741B47',
    border: '#741B47',
  },
  {
    label: 'Pink-900',
    value: '#4C1130',
    border: '#4C1130',
  },
]

export const useHighlighter = (editor: Editor | null) => {
  const markAvailable = isMarkInSchema('highlight', editor)

  const getActiveColor = React.useCallback(() => {
    if (!editor) return null
    if (!editor.isActive('highlight')) return null
    const attrs = editor.getAttributes('highlight')
    return attrs.color || null
  }, [editor])

  const toggleHighlight = React.useCallback(
    (color: string) => {
      if (!markAvailable || !editor) return
      if (color === 'none') {
        editor.chain().focus().unsetMark('highlight').run()
      } else {
        editor.chain().focus().toggleMark('highlight', { color }).run()
      }
    },
    [markAvailable, editor],
  )

  return {
    markAvailable,
    getActiveColor,
    toggleHighlight,
  }
}

export const HighlighterButton = React.forwardRef<
  HTMLButtonElement,
  ButtonProps
>(({ className, children, ...props }, ref) => {
  return (
    <Button
      type="button"
      className={className}
      data-style="ghost"
      data-appearance="default"
      role="button"
      tabIndex={-1}
      aria-label="Highlight text"
      tooltip="Highlight"
      data-size='large'
      ref={ref}
      {...props}
    >
     <HighlighterIcon className="tiptap-button-icon" />
      <ChevronDownIcon width={'16px'} height={'16px'} className="tiptap-button-dropdown-small" />
    </Button>
  )
})

export function HighlightContent({
  editor: providedEditor,
  colors = DEFAULT_HIGHLIGHT_COLORS,
  onClose,
}: {
  editor?: Editor | null
  colors?: HighlightColor[]
  onClose?: () => void
}) {
  const editor = useTiptapEditor(providedEditor)
  const ROWS_PER_COLUMN = 6;
    // Tính padding để cột cuối không bị "lệch"
  const remainder = colors.length % ROWS_PER_COLUMN;
  const paddedItems =
    remainder === 0
      ? colors
      : [...colors, ...Array(ROWS_PER_COLUMN - remainder).fill(null)];
  const containerRef = React.useRef<HTMLDivElement>(null)

  const { getActiveColor, toggleHighlight } = useHighlighter(editor)
  const activeColor = getActiveColor()

  const menuItems = React.useMemo(
    () => [...colors, { label: 'Remove highlight', value: 'none' }],
    [colors],
  )

  const { selectedIndex } = useMenuNavigation({
    containerRef,
    items: menuItems,
    orientation: 'both',
    onSelect: (item) => {
      toggleHighlight(item.value)
      onClose?.()
    },
    onClose,
    autoSelectFirstItem: false,
  })

  return (
    <div ref={containerRef} className="tiptap-highlight-content" tabIndex={0}>
      <div className='sapp-background-none' onClick={() => toggleHighlight('none')}>
        None
      </div>
      <div className="sapp-background-dropdown-menu" data-orientation="horizontal">
        {colors.map((color, index) => (
          <Button
            key={color.value}
            type="button"
            role="menuitem"
            data-active-state={activeColor === color.value ? 'on' : 'off'}
            aria-label={`${color.label} highlight color`}
            tabIndex={index === selectedIndex ? 0 : -1}
            data-style="ghost"
            onClick={() => toggleHighlight(color.value)}
            data-highlighted={selectedIndex === index}
            className=''
          >
            <span
              className="tiptap-button-highlight"
              style={
                { '--highlight-color': color.value } as React.CSSProperties
              }
            >
              {
              activeColor === color.value ? <CheckIcon style={{ color: 'white' }} /> : <span />
            }
            </span>
            
          </Button>
        ))}
      </div>

      {/* <Separator />

      <div className="tiptap-button-group">
        <Button
          onClick={() => toggleHighlight('none')}
          aria-label="Remove highlight"
          tabIndex={selectedIndex === colors.length ? 0 : -1}
          type="button"
          role="menuitem"
          data-style="ghost"
          data-highlighted={selectedIndex === colors.length}
        >
          <BanIcon className="tiptap-button-icon" />
        </Button>
      </div> */}
    </div>
  )
}

export interface HighlightPopoverProps extends Omit<ButtonProps, 'type'> {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor | null
  /**
   * The highlight colors to display in the popover.
   */
  colors?: HighlightColor[]
  /**
   * Whether to hide the highlight popover.
   */
  hideWhenUnavailable?: boolean
}

export function HighlightPopover({
  editor: providedEditor,
  colors = DEFAULT_HIGHLIGHT_COLORS,
  hideWhenUnavailable = false,
  ...props
}: HighlightPopoverProps) {
  const editor = useTiptapEditor(providedEditor)

  const { markAvailable } = useHighlighter(editor)
  const [isOpen, setIsOpen] = React.useState(false)

  const isDisabled = React.useMemo(() => {
    if (!markAvailable || !editor) {
      return true
    }

    return (
      editor.isActive('code') ||
      editor.isActive('codeBlock') ||
      editor.isActive('imageUpload')
    )
  }, [markAvailable, editor])

  const canSetMark = React.useMemo(() => {
    if (!editor || !markAvailable) return false

    try {
      return editor.can().setMark('highlight')
    } catch {
      return false
    }
  }, [editor, markAvailable])

  const isActive = editor?.isActive('highlight') || false

  const show = React.useMemo(() => {
    if (hideWhenUnavailable) {
      if (isNodeSelection(editor?.state.selection) || !canSetMark) {
        return false
      }
    }

    return true
  }, [hideWhenUnavailable, editor, canSetMark])

  if (!show || !editor || !editor.isEditable) {
    return null
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <HighlighterButton
          disabled={isDisabled}
          data-active-state={isActive ? 'on' : 'off'}
          data-disabled={isDisabled}
          aria-pressed={isActive}
          {...props}
        />

      </PopoverTrigger>

      <PopoverContent aria-label="Highlight colors">
        <HighlightContent
          editor={editor}
          colors={colors}
          onClose={() => setIsOpen(false)}
        />
      </PopoverContent>
    </Popover>
  )
}

HighlighterButton.displayName = 'HighlighterButton'

export default HighlightPopover
